import type { DimensionModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import DimensionSelector from "@v2/features/modifiers/base/DimensionSelector";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { useComputedElementDimensions } from "@v2/features/modifiers/store/ModifiersStoreContext";
import { deriveDimensionFromClassName } from "replo-runtime/shared/utils/dimensions";

interface WidthModifierProps {
  onChange: (modifiers: DimensionModifierValue[]) => void;
}

const WidthModifier: React.FC<WidthModifierProps> = ({ onChange }) => {
  const computedElementDimensions = useComputedElementDimensions();
  const optimisticWidth = useOptimisticModifierValue({
    modifierType: { type: "dimension", side: "width", value: 0 },
    extractor: (elementDesignation) => {
      // Check className first
      const classDerived = deriveDimensionFromClassName(
        elementDesignation.element.props?.className ?? undefined,
        "width",
      );
      if (classDerived !== null) {
        return classDerived;
      }

      // Then check styleOverrides
      const widthValue = elementDesignation.element.styleOverrides?.width;
      if (widthValue === "auto") {
        return "auto";
      }
      if (widthValue === "100%") {
        return "full";
      }

      return null;
    },
  });

  const currentValue = React.useMemo((): number | "auto" | "full" | null => {
    return optimisticWidth ?? null;
  }, [optimisticWidth]);

  const handleValueChange = (width: number | "auto" | "full" | null) => {
    if (width !== null) {
      onChange([
        {
          type: "dimension" as const,
          side: "width",
          value: width,
        },
      ]);
    }
  };

  return (
    <div className="width-modifier">
      <DimensionSelector
        type="width"
        value={currentValue}
        onSelect={handleValueChange}
        computedDimension={computedElementDimensions?.width ?? null}
      />
    </div>
  );
};

export default WidthModifier;
